import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import Typography from '../typography/Typography';
import Link from '../link/Link';
import { createMarkup, useBackgroundColor } from '../../utils';
import { HelpTopic } from '../../types';

// Interface

export interface HelpTopicsProps {
  helpTopics: HelpTopic[];
}

interface StyledHeaderProps {
  backgroundColor: ThemeColorsKeys;
}

// Styles

const StyledHelpTopics = styled.ul`
  ${({ theme: { space } }) => css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: ${space.fluidXSmallToSmall};
    row-gap: ${space.fluidXSmallToMedium};
  `};
`;

const StyledHelpTopic = styled.li`
  ${({ theme: { colors, media, borderRadii } }) => css`
    border: 1px solid ${colors.darkBlue};
    border-radius: ${borderRadii.small};
    overflow: hidden;
    position: relative;
    transition: all 0.2s ease-in-out;
    display: flex;
    flex-direction: column;

    &:hover {
      box-shadow: 0 0 0 1px ${colors.darkBlue};
      opacity: 0.9;
    }

    @media (min-width: ${media.medium}) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: ${media.xLarge}) {
      grid-template-columns: 345px auto;
    }
  `};
`;

const StyledHeader = styled.div<StyledHeaderProps>`
  ${({ theme: { space }, backgroundColor }) => css`
    padding: ${space.fluidMediumToLarge};
    ${useBackgroundColor(backgroundColor)}
    padding: ${space.fluidXxSmallToMedium};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${space.xSmall};

    h3 {
      min-height: 72px;
    }

    a:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    @media (min-width: 400px) {
      h3 {
        min-height: 64px;
      }
    }
    @media (min-width: 570px) {
      h3 {
        min-height: 32px;
      }
    }
  `};
`;

const StyledBody = styled.div`
  ${({ theme: { space, colors, media, maxMedia } }) => css`
    padding: ${space.fluidXxSmallToMedium};
    ${useBackgroundColor('white')}
    flex-grow: 1;

    @media (max-width: ${maxMedia.medium}) {
      border-top: 1px solid ${colors.darkBlue};
    }

    @media (min-width: ${media.medium}) {
      border-left: 1px solid ${colors.darkBlue};
    }
  `};
`;

// JSX

const HelpTopicCard: FC<HelpTopic> = ({
  name,
  description,
  url,
  backgroundColor,
}) => (
  <StyledHelpTopic>
    <StyledHeader backgroundColor={backgroundColor}>
      <Typography component="h3" variant="headingXSmall">
        {name}
      </Typography>
      <Link
        url={url}
        a11yTitle={name}
        dataLayerClickContext={`Help center topic card - ${name}`}
      />
    </StyledHeader>
    <StyledBody>
      <Typography component="div" variant="bodySmallToBody">
        {createMarkup(description)}
      </Typography>
    </StyledBody>
  </StyledHelpTopic>
);

export const HelpTopics: FC<HelpTopicsProps> = ({ helpTopics }) => (
  <StyledHelpTopics>
    {helpTopics.map((helpTopic, index) => (
      <HelpTopicCard key={index} {...helpTopic} />
    ))}
  </StyledHelpTopics>
);

export default HelpTopics;
